import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { ArrowDown, ArrowUpRight } from "lucide-react";
import { CONTACT, IMG } from "../data/site";

const lineAnim = {
  hidden: { y: "110%" },
  show: (i) => ({
    y: 0,
    transition: { duration: 0.9, ease: [0.22, 1, 0.36, 1], delay: 0.35 + i * 0.14 },
  }),
};

const Line = ({ children, i, className = "" }) => (
  <span className="block overflow-hidden">
    <motion.span custom={i} variants={lineAnim} initial="hidden" animate="show" className={`block ${className}`}>
      {children}
    </motion.span>
  </span>
);

export const KineticHero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yImg = useTransform(scrollYProgress, [0, 1], [0, 120]);
  const yText = useTransform(scrollYProgress, [0, 1], [0, -60]);

  return (
    <section ref={ref} id="top" data-testid="hero-section" className="relative min-h-screen flex flex-col justify-end overflow-hidden noise pt-16">
      <div className="absolute top-24 right-[-4rem] md:right-10 w-56 md:w-80 h-56 md:h-80 rounded-full bg-[#FFB6C1] blur-[100px] opacity-60" />
      <div className="absolute bottom-40 left-[-6rem] w-72 h-72 rounded-full bg-[#0047FF] blur-[140px] opacity-20" />

      <motion.div style={{ y: yText }} className="relative z-10 max-w-7xl mx-auto w-full px-5 md:px-10 pt-16 md:pt-24">
        <Line i={0} className="font-display text-xs md:text-sm uppercase tracking-[0.3em] font-semibold text-[#0047FF] mb-6">
          Arnav · Branding & Marketing Specialist
        </Line>
        <h1 className="font-display font-black tracking-tighter leading-[0.92] text-[13.5vw] sm:text-6xl md:text-7xl lg:text-[6.5rem]">
          <Line i={1}>I make brands</Line>
          <Line i={2}>
            people <span className="text-[#0047FF]">remember</span>
          </Line>
          <Line i={3}>
            and <span className="text-stroke-blue">buy from.</span>
          </Line>
        </h1>
        <div className="mt-8 md:mt-10 flex flex-col md:flex-row md:items-end gap-8 md:gap-16">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.1, duration: 0.7 }}
            className="text-base md:text-lg text-black/60 max-w-md leading-relaxed"
          >
            Branding, websites, content and growth, designed by one person who cares about your numbers as much as your look.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.25, duration: 0.7 }}
            className="flex items-center gap-4"
          >
            <a
              href="#work"
              data-testid="hero-view-work-btn"
              className="whitespace-nowrap font-display font-bold px-6 md:px-7 py-4 rounded-full bg-[#0047FF] text-white flex items-center gap-2 hover:bg-[#0A0A0A] transition-colors"
            >
              View My Work <ArrowDown size={18} />
            </a>
            <a
              href={CONTACT.whatsapp}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hero-lets-talk-btn"
              className="whitespace-nowrap font-display font-bold px-6 md:px-7 py-4 rounded-full border-2 border-[#0A0A0A] hover:border-[#0047FF] hover:text-[#0047FF] transition-colors flex items-center gap-2"
            >
              Let's Talk <ArrowUpRight size={18} />
            </a>
          </motion.div>
        </div>
      </motion.div>

      <motion.div
        style={{ y: yImg }}
        initial={{ opacity: 0, y: 80 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 1.0, duration: 1, ease: [0.22, 1, 0.36, 1] }}
        className="relative z-0 max-w-7xl mx-auto w-full px-5 md:px-10 mt-12 md:mt-16"
      >
        <div className="grid grid-cols-3 gap-3 md:gap-6 items-end pb-0">
          <div className="overflow-hidden rounded-t-lg aspect-[3/4] md:aspect-[4/5]">
            <img src={IMG.aiCampaign} alt="AI campaign visual" className="w-full h-full object-cover" loading="eager" />
          </div>
          <div className="overflow-hidden rounded-t-lg aspect-[3/4] md:aspect-[4/5] translate-y-6 md:translate-y-10">
            <img src={IMG.portrait} alt="Arnav portrait" className="w-full h-full object-cover" loading="eager" />
          </div>
          <div className="overflow-hidden rounded-t-lg aspect-[3/4] md:aspect-[4/5]">
            <img src={IMG.socialCreative} alt="Social creative work" className="w-full h-full object-cover" loading="eager" />
          </div>
        </div>
      </motion.div>
    </section>
  );
};
